import React, { useState } from 'react'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import {
  Building,
  MapPin,
  Target,
  TrendingUp,
  AlertTriangle,
  Info
} from 'lucide-react'
import type { ConcentrationRisk } from '@/types/risk-assessment'

interface ConcentrationRiskVisualizationsProps {
  concentration_risk: ConcentrationRisk
  onDrillDown?: (category: string, item: string) => void
  compact?: boolean
}

export function ConcentrationRiskVisualizations({
  concentration_risk,
  onDrillDown,
  compact = false
}: ConcentrationRiskVisualizationsProps) {
  const [activeChart, setActiveChart] = useState<'geographic' | 'property_type' | 'market_exposure'>('geographic')

  // Color schemes for charts
  const RISK_COLORS = {
    low: '#10b981',     // green
    medium: '#f59e0b',  // yellow/amber
    high: '#ef4444'     // red
  }

  const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6b7280']

  // Safe data preparation with empty state handling
  const geographicData = React.useMemo(() => {
    const locations = concentration_risk?.geographic?.locations || []
    console.log('🌍 ConcentrationRiskVisualizations - Geographic locations:', locations.length)

    if (locations.length === 0) {
      console.warn('⚠️ No geographic locations found for visualization')
      return []
    }

    return locations.map((location, index) => ({
      name: `${location.city || 'Unknown'}, ${location.state || 'Unknown'}`,
      value: typeof location.percentage === 'number' ? location.percentage : 0,
      properties: typeof location.property_count === 'number' ? location.property_count : 0,
      total_value: typeof location.total_value === 'number' ? location.total_value : 0,
      risk_level: location.risk_level || 'medium',
      fill: RISK_COLORS[location.risk_level || 'medium'] || CHART_COLORS[index % CHART_COLORS.length]
    }))
  }, [concentration_risk?.geographic?.locations])

  const propertyTypeData = React.useMemo(() => {
    const types = concentration_risk?.property_type?.types || []
    console.log('🏘️ ConcentrationRiskVisualizations - Property types:', types.length)

    if (types.length === 0) {
      console.warn('⚠️ No property types found for visualization')
      return []
    }

    return types.map((type, index) => ({
      name: (type.type || 'Unknown Type').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: typeof type.percentage === 'number' ? type.percentage : 0,
      properties: typeof type.property_count === 'number' ? type.property_count : 0,
      total_value: typeof type.total_value === 'number' ? type.total_value : 0,
      risk_level: type.risk_level || 'medium',
      fill: RISK_COLORS[type.risk_level || 'medium'] || CHART_COLORS[index % CHART_COLORS.length]
    }))
  }, [concentration_risk?.property_type?.types])

  const marketExposureData = React.useMemo(() => {
    const markets = concentration_risk?.market_exposure?.markets || []
    console.log('📈 ConcentrationRiskVisualizations - Markets:', markets.length)

    if (markets.length === 0) {
      console.warn('⚠️ No market exposure data found for visualization')
      return []
    }

    return markets.map((market, index) => ({
      name: market.market_name || 'Unknown Market',
      exposure: typeof market.exposure_percentage === 'number' ? market.exposure_percentage : 0,
      health_score: typeof market.market_health_score === 'number' ? market.market_health_score : 0,
      trend: market.growth_trend || 'stable',
      employment: typeof market.employment_rate === 'number' ? market.employment_rate : 0,
      population_growth: typeof market.population_growth === 'number' ? market.population_growth : 0,
      fill: CHART_COLORS[index % CHART_COLORS.length]
    }))
  }, [concentration_risk?.market_exposure?.markets])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label || data.name}</p>
          <p className="text-blue-600">
            Allocation: <span className="font-medium">{formatPercentage(data.value || data.exposure)}</span>
          </p>
          {data.properties && (
            <p className="text-gray-600">
              Properties: <span className="font-medium">{data.properties}</span>
            </p>
          )}
          {data.total_value && (
            <p className="text-gray-600">
              Value: <span className="font-medium">{formatCurrency(data.total_value)}</span>
            </p>
          )}
          {data.health_score && (
            <p className="text-green-600">
              Market Health: <span className="font-medium">{data.health_score}/100</span>
            </p>
          )}
        </div>
      )
    }
    return null
  }

  // Empty state components
  const EmptyStateCard = ({ title, message }: { title: string; message: string }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
      <div className="flex flex-col items-center justify-center h-48 text-gray-500">
        <AlertTriangle className="w-8 h-8 mb-2" />
        <p className="text-sm text-center">{message}</p>
      </div>
    </div>
  )

  if (compact) {
    return (
      <div className="space-y-6">
        {/* Geographic Concentration - Compact */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Geographic Allocation</h4>
          {geographicData.length > 0 ? (
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={geographicData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {geographicData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <MapPin className="w-8 h-8 mb-2" />
              <p className="text-sm text-center">No geographic data available</p>
              <p className="text-xs text-gray-400 mt-1">Add properties to see geographic distribution</p>
            </div>
          )}
        </div>

        {/* Property Type Concentration - Compact */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Property Type Mix</h4>
          {propertyTypeData.length > 0 ? (
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={propertyTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <Building className="w-8 h-8 mb-2" />
              <p className="text-sm text-center">No property type data available</p>
              <p className="text-xs text-gray-400 mt-1">Add properties to see type distribution</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'geographic', name: 'Geographic', icon: MapPin },
            { id: 'property_type', name: 'Property Types', icon: Building },
            { id: 'market_exposure', name: 'Market Exposure', icon: TrendingUp }
          ].map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveChart(tab.id as any)}
                className={`
                  flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm
                  ${activeChart === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Geographic Concentration */}
      {activeChart === 'geographic' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Distribution</h3>
            {geographicData.length > 0 ? (
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={geographicData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="value"
                      onClick={(data) => onDrillDown?.('geographic', data.name || '')}
                    >
                      {geographicData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                <MapPin className="w-12 h-12 mb-4" />
                <h4 className="text-lg font-medium mb-2">No Geographic Data</h4>
                <p className="text-sm text-center max-w-md">
                  Add properties to your portfolio to see geographic distribution and concentration analysis.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Risk Score */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Concentration Risk Score</h4>
                <span className={`text-2xl font-bold ${
                  concentration_risk.geographic.concentration_score <= 30 ? 'text-green-600' :
                  concentration_risk.geographic.concentration_score <= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {concentration_risk.geographic.concentration_score}/100
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div
                  className={`h-3 rounded-full ${
                    concentration_risk.geographic.concentration_score <= 30 ? 'bg-green-500' :
                    concentration_risk.geographic.concentration_score <= 60 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${concentration_risk.geographic.concentration_score}%` }}
                />
              </div>
              <p className="text-sm text-gray-600">
                {concentration_risk.geographic.diversification_recommendation}
              </p>
            </div>

            {/* Top Concentrations */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Top Market Concentrations</h4>
              {geographicData.length > 0 ? (
                <div className="space-y-3">
                  {geographicData.slice(0, 3).map((location, index) => (
                    <div key={location.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: location.fill }} />
                        <span className="font-medium text-gray-900">{location.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900">{formatPercentage(location.value)}</div>
                        <div className="text-sm text-gray-500">{location.properties} properties</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                  <Target className="w-6 h-6 mb-2" />
                  <p className="text-sm text-center">No concentration data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Property Type Concentration */}
      {activeChart === 'property_type' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Type Distribution</h3>
            {propertyTypeData.length > 0 ? (
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={propertyTypeData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="value"
                      onClick={(data) => onDrillDown?.('property_type', data.name || '')}
                    >
                      {propertyTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                <Building className="w-12 h-12 mb-4" />
                <h4 className="text-lg font-medium mb-2">No Property Type Data</h4>
                <p className="text-sm text-center max-w-md">
                  Add properties to your portfolio to see property type distribution and concentration analysis.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Property Type Risk Assessment */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Property Type Risk Analysis</h4>
              {propertyTypeData.length > 0 ? (
                <div className="space-y-4">
                  {propertyTypeData.map((type) => (
                    <div key={type.name} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Building className={`w-4 h-4 ${
                          type.risk_level === 'low' ? 'text-green-600' :
                          type.risk_level === 'medium' ? 'text-yellow-600' :
                          'text-red-600'
                        }`} />
                        <span className="font-medium text-gray-900">{type.name}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">{formatPercentage(type.value)}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          type.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                          type.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {type.risk_level.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                  <Building className="w-6 h-6 mb-2" />
                  <p className="text-sm text-center">No property type analysis available</p>
                </div>
              )}
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Diversification Recommendations</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <p className="text-sm text-gray-700">
                    {concentration_risk.property_type.recommendation}
                  </p>
                </div>
                {concentration_risk.property_type.concentration_score > 60 && (
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <p className="text-sm text-orange-800">
                      High concentration detected. Consider diversifying into different property types.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Market Exposure */}
      {activeChart === 'market_exposure' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Market Exposure Analysis</h3>
            {marketExposureData.length > 0 ? (
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={marketExposureData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="exposure"
                      fill="#3b82f6"
                      onClick={(data) => onDrillDown?.('market_exposure', data.name || '')}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                <TrendingUp className="w-12 h-12 mb-4" />
                <h4 className="text-lg font-medium mb-2">No Market Exposure Data</h4>
                <p className="text-sm text-center max-w-md">
                  Add properties to your portfolio to see market exposure and correlation analysis.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Market Health Scores */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Market Health Indicators</h4>
              {marketExposureData.length > 0 ? (
                <div className="space-y-4">
                  {marketExposureData.map((market) => (
                    <div key={market.name} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{market.name}</span>
                        <div className="flex items-center space-x-2">
                          <Target className={`w-4 h-4 ${
                            market.trend === 'growing' ? 'text-green-600' :
                            market.trend === 'stable' ? 'text-yellow-600' :
                            'text-red-600'
                          }`} />
                          <span className="text-sm font-medium">{formatPercentage(market.exposure)}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Health Score:</span>
                          <div className="font-medium">{market.health_score}/100</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Growth Trend:</span>
                          <div className={`font-medium ${
                            market.trend === 'growing' ? 'text-green-600' :
                            market.trend === 'stable' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {(market.trend || '').charAt(0).toUpperCase() + (market.trend || '').slice(1)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Employment:</span>
                          <div className="font-medium">{formatPercentage(market.employment)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Pop. Growth:</span>
                          <div className="font-medium">{formatPercentage(market.population_growth)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                  <TrendingUp className="w-6 h-6 mb-2" />
                  <p className="text-sm text-center">No market health data available</p>
                </div>
              )}
            </div>

            {/* Correlation Analysis */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Market Correlation</h4>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-600">Overall Correlation Score</span>
                <span className={`text-lg font-bold ${
                  concentration_risk.market_exposure.correlation_score <= 0.3 ? 'text-green-600' :
                  concentration_risk.market_exposure.correlation_score <= 0.6 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {formatPercentage(concentration_risk.market_exposure.correlation_score * 100)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${
                    concentration_risk.market_exposure.correlation_score <= 0.3 ? 'bg-green-500' :
                    concentration_risk.market_exposure.correlation_score <= 0.6 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${concentration_risk.market_exposure.correlation_score * 100}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Lower correlation indicates better diversification across market cycles.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}